import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { SelectableCard } from '@ui/SelectableCard';
import { TextField } from '@ui/inputs';
import { createTimeInstance } from '@utils/time';
import { SCOPE_OPTIONS } from '@services/scoreSpecs';
import { DateWindow } from '@features/scoreBackfill/DateWindow';
import { PREVIEW_UNIVERSE_KINDS, PREVIEW_WINDOW_PRESETS, parseTickers } from './previewSelection';
import { Consequence, MicroLabel, Stack } from './styles';

// What a preview run is pointed AT: a universe, a window, and a scope.
//
// Since fapi #492 the dock no longer hands the engine document ids — it describes a selection and
// the spec's own trigger decides which documents that means. Every value on screen comes from the
// `selection` prop and every edit leaves through `onChange`, so the body the free resolve sends and
// the body the paid run sends are built from the same object by buildPreviewBody. Holding a copy
// here is how the count an operator read stops describing the run they launched.
//
// Cards rather than a select for both radiogroups, because each choice costs money in a way the
// label alone does not convey: a universe decides how many companies can fire, and a scope decides
// how many of the 25 company-units one company is allowed to eat.

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// DateWindow takes no `disabled` prop — its presets are SkeletonButtons and its pickers are
// react-date-picker inputs, none of which read one. A native disabled fieldset turns all of them
// off for free. Without it the preset row stays live during a paid run and a stray click rewrites
// the window the run in flight was launched against.
const WindowGuard = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
`;

const UNIVERSE_CORPUS_DESC_ID = 'score-spec-preview-universe-corpus-desc';
const UNIVERSE_TICKERS_DESC_ID = 'score-spec-preview-universe-tickers-desc';
const SCOPE_LATEST_DESC_ID = 'score-spec-preview-scope-latest-desc';
const SCOPE_FULL_DESC_ID = 'score-spec-preview-scope-full-desc';

export const PreviewScopeControls = ({ selection, onChange, disabled }) => {
  const { universeKind, tickerText, fromDate, toDate, scope } = selection || {};

  const tickers = parseTickers(tickerText);
  const tickerHint =
    tickers.length === 0
      ? 'Symbols separated by spaces or commas.'
      : `${tickers.length} symbol${tickers.length === 1 ? '' : 's'}: ${tickers.join(', ')}`;

  // The upper bound is today. A preview cannot read a document that has not been published, and a
  // future `to` silently widens the window the operator thinks they set.
  const maxDate = createTimeInstance().toDate();

  return (
    <div>
      <MicroLabel>Universe</MicroLabel>

      <Container top={12}>
        {/* Values come from PREVIEW_UNIVERSE_KINDS, never a literal: the same two strings are what
            buildPreviewBody puts on the wire, and a typo here resolves an empty universe rather
            than failing. */}
        <Stack role="radiogroup" aria-label="Universe">
          <SelectableCard selected={universeKind === PREVIEW_UNIVERSE_KINDS.WHOLE_CORPUS} disabled={disabled}>
            <input
              type="radio"
              name="preview-universe"
              checked={universeKind === PREVIEW_UNIVERSE_KINDS.WHOLE_CORPUS}
              disabled={disabled}
              onChange={() => onChange({ universeKind: PREVIEW_UNIVERSE_KINDS.WHOLE_CORPUS })}
              aria-describedby={UNIVERSE_CORPUS_DESC_ID}
            />
            <OptionText>
              <Body5 bold>Whole corpus</Body5>
              <Body7 id={UNIVERSE_CORPUS_DESC_ID} isLowContrast>
                Every company we cover. The window decides which of them fire.
              </Body7>
            </OptionText>
          </SelectableCard>

          <SelectableCard selected={universeKind === PREVIEW_UNIVERSE_KINDS.TICKERS} disabled={disabled}>
            <input
              type="radio"
              name="preview-universe"
              checked={universeKind === PREVIEW_UNIVERSE_KINDS.TICKERS}
              disabled={disabled}
              onChange={() => onChange({ universeKind: PREVIEW_UNIVERSE_KINDS.TICKERS })}
              aria-describedby={UNIVERSE_TICKERS_DESC_ID}
            />
            <OptionText>
              <Body5 bold>Tickers</Body5>
              <Body7 id={UNIVERSE_TICKERS_DESC_ID} isLowContrast>
                A specific list of symbols you name.
              </Body7>
            </OptionText>
          </SelectableCard>
        </Stack>
      </Container>

      <Container top={8}>
        <Consequence>
          An index universe is not offered here. The index list is served only by the backfill API, which is behind a
          different permission — asking for it would sign a spec author out of the editor. Name the companies you care
          about, or take whatever the window turns up.
        </Consequence>
      </Container>

      {universeKind === PREVIEW_UNIVERSE_KINDS.TICKERS && (
        <Container top={16}>
          {/* The hint reads back the parsed list, not the raw box: parseTickers upper-cases, splits
              on commas or spaces and drops duplicates, so what the operator typed and what goes on
              the wire are not the same string. */}
          <TextField
            label="Tickers"
            mono
            value={tickerText}
            onChange={(value) => onChange({ tickerText: value })}
            disabled={disabled}
            hint={tickerHint}
          />
        </Container>
      )}

      <Container top={24}>
        <MicroLabel>Preview window</MicroLabel>
        <Container top={12}>
          <WindowGuard disabled={disabled}>
            {/* PREVIEW_WINDOW_PRESETS is mandatory. DateWindow's default set tops out at a Max that,
                with no minDate, resolves to a FIFTEEN-year window — one click, then one click on a
                button that spends real money on up to 25 model calls. The preview set stops at 1y. */}
            <DateWindow
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={(date) => onChange({ fromDate: date })}
              onToChange={(date) => onChange({ toDate: date })}
              presets={PREVIEW_WINDOW_PRESETS}
              legend="Preview window"
              maxDate={maxDate}
            />
          </WindowGuard>
        </Container>
      </Container>

      <Container top={24}>
        <MicroLabel>Scope</MicroLabel>
        <Container top={12}>
          <Stack role="radiogroup" aria-label="Scope">
            <SelectableCard selected={scope === SCOPE_OPTIONS.LATEST_PER_COMPANY} disabled={disabled}>
              <input
                type="radio"
                name="preview-scope"
                checked={scope === SCOPE_OPTIONS.LATEST_PER_COMPANY}
                disabled={disabled}
                onChange={() => onChange({ scope: SCOPE_OPTIONS.LATEST_PER_COMPANY })}
                aria-describedby={SCOPE_LATEST_DESC_ID}
              />
              <OptionText>
                <Body5 bold>Latest per company</Body5>
                <Body7 id={SCOPE_LATEST_DESC_ID} isLowContrast>
                  One occasion per company, its most recent in the window. Twenty-five different companies rather than
                  one company twenty-five times.
                </Body7>
              </OptionText>
            </SelectableCard>

            <SelectableCard selected={scope === SCOPE_OPTIONS.FULL_HISTORY} disabled={disabled}>
              <input
                type="radio"
                name="preview-scope"
                checked={scope === SCOPE_OPTIONS.FULL_HISTORY}
                disabled={disabled}
                onChange={() => onChange({ scope: SCOPE_OPTIONS.FULL_HISTORY })}
                aria-describedby={SCOPE_FULL_DESC_ID}
              />
              <OptionText>
                <Body5 bold>Full history</Body5>
                <Body7 id={SCOPE_FULL_DESC_ID} isLowContrast>
                  Every occasion in the window. One company that reported four times uses four of your 25 company-units.
                </Body7>
              </OptionText>
            </SelectableCard>
          </Stack>
        </Container>
      </Container>
    </div>
  );
};
